import { useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronRight,
  Eye,
  MousePointerClick,
  Paintbrush,
  Power,
  RefreshCw,
  Send,
  Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Separator } from '@/shared/components/ui/separator'
import { cn } from '@/shared/lib/utils'
import { EmailEditor } from '@/admin/components/email-editor'

import { PreviewDialog } from './preview-dialog'
import { promptToken } from './prompt-token'
import { PromptsEditor } from './prompts-editor'
import { RequirementsEditor } from './requirements-editor'
import { TemplateAttachmentsEditor } from './template-attachments-editor'
import { slugify } from './slug'
import {
  useCreateTemplate,
  useDeleteTemplate,
  useDeleteTemplateOverride,
  useEmailTemplate,
  useEmailTemplateTags,
  useEmailTemplateVariables,
  useSaveTemplateOverride,
  useTestSendTemplate,
  useUpdateTemplate,
} from './use-email-templates'
import type {
  EvidenceRequestPayload,
  OutputMode,
  PromptDefinition,
  TemplateDetail,
  TemplateKind,
} from './types'

const EMPTY_PAYLOAD: EvidenceRequestPayload = { requirements: [], outro_html: '', photo_tips: [] }

const BLANK_TEMPLATE: TemplateDetail = {
  id: 0,
  slug: '',
  name: '',
  kind: 'notification',
  subject: '',
  body_html: '',
  body_text: '',
  sms_body: '',
  structured_payload: {},
  variables_schema: {},
  tags: [],
  output_modes: ['email_body'],
  prompts: [],
  tracking_code: '',
  attachments: [],
  version: 1,
  is_active: true,
  is_platform: false,
  is_slug_bound: false,
  updated_at: '',
}

// Friendly names for the fields this editor posts, used wherever a field is
// named to an admin - a save error pointing at the right section, and which
// parts follow the platform template. Labels mirror the form's own headings, so
// nothing surfaces a column name like "body_html". Unknown fields fall back to
// the raw key.
const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  slug: 'Name',
  subject: 'Subject line',
  body_html: 'Message',
  body_text: 'Plain-text version',
  sms_body: 'SMS version',
  structured_payload: 'What to collect',
  output_modes: 'What gets sent',
  tags: 'Tags',
}

/**
 * Turn a save failure into an accurate toast message. A DRF 400 carries a
 * `result` object of `{ field: [messages] }`; we surface the first field error
 * (e.g. "Message: This field may not be blank.") instead of a generic line
 * that wrongly points every failure at the requirements list.
 */
function describeSaveError(err: unknown): string {
  const response = (
    err as { response?: { status?: number; data?: unknown } }
  )?.response
  if (response?.status !== 400) return "Couldn't save. Try again."
  const data = response.data as { message?: string; result?: unknown } | undefined
  const result = data?.result
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const entries = Object.entries(result as Record<string, unknown>)
    if (entries.length) {
      const [field, messages] = entries[0]
      const label = FIELD_LABELS[field] ?? field
      const first = Array.isArray(messages)
        ? String(messages[0])
        : String(messages)
      return `${label}: ${first}`
    }
  }
  return data?.message || 'Validation failed. Check the highlighted fields.'
}

// ─── Variables picker ──────────────────────────────────────────────

// Inserted by the "Requested items list" picker entry. Renders the documents
// and photos this send is actually asking for (see the evidence_request
// renderer's requirement context), so the list can never go stale the way a
// hand-authored one does. Falls back to nothing when a send has no items.
const REQUESTED_ITEMS_BLOCK = `{% if document_requirements %}
<p><strong>Documents</strong></p>
<ul>
{% for r in document_requirements %}
  <li>{{ r.label }}{% if r.description %} - {{ r.description }}{% endif %}</li>
{% endfor %}
</ul>
{% endif %}
{% if photo_requirements %}
<p><strong>Photos</strong></p>
<ul>
{% for r in photo_requirements %}
  <li>{{ r.label }}{% if r.description %} - {{ r.description }}{% endif %}</li>
{% endfor %}
</ul>
{% endif %}`

type VariableSpec = {
  label: string
  value: string
  /** 'text' inserts the raw {{ … }} token. 'link' wraps it in a plain <a>
   *  tag. 'button' wraps it in an inline-styled <a> rendered as a CTA
   *  button - survives Gmail / Outlook / Apple Mail and the preview iframe. */
  kind?: 'text' | 'link' | 'button'
  /** Default link/button text when kind='link' | 'button'. */
  linkLabel?: string
  /** Catalog group this variable belongs to (common / claim / …). */
  group?: string
}

/**
 * Render an inline-styled <a> that displays as a CTA button. Uses inline
 * styles (not classes) because email clients strip <style> blocks. Wrapped
 * in a <p> so block-level rendering is consistent across clients.
 */
function buildEmailButton(href: string, label: string): string {
  const buttonStyle = [
    'display:inline-block',
    'padding:12px 24px',
    "background-color:{{ brand_primary_color|default:'#1c5a48' }}",
    'color:#ffffff',
    'text-decoration:none',
    'border-radius:6px',
    'font-weight:600',
    "font-family:'Geist',Arial,Helvetica,sans-serif",
    'font-size:14px',
    'line-height:1.2',
    'mso-padding-alt:0',
  ].join(';')
  return `<p style="margin:16px 0;"><a href="${href}" target="_blank" rel="noopener noreferrer" style="${buttonStyle}">${label}</a></p>`
}

function VariablesPicker({
  hint,
  templateKind,
  prompts = [],
}: {
  hint?: string
  templateKind: TemplateKind
  /** The template's fill-ins, offered as their `{{ prompts.<key> }}` tokens. */
  prompts?: PromptDefinition[]
}) {
  const { t } = useTranslation()
  // Variables come from the backend catalog (single source of truth), so the
  // list always matches what the renderer can actually supply - including
  // tenant-configured EAV fields - rather than a hard-coded array.
  const { data: catalog = [] } = useEmailTemplateVariables()
  const variables: VariableSpec[] = catalog.map((v) => ({
    label: v.description || v.name,
    value: `{{ ${v.name} }}`,
    group: v.group,
  }))
  // Evidence templates: offer the upload link as a ready-made CTA button. This
  // is a presentation affordance - the catalog still lists `session`.
  if (templateKind === 'evidence_request') {
    variables.push({
      label: 'Upload link',
      value: '{{ session.url }}',
      kind: 'button',
      linkLabel: 'Upload your photos',
      group: 'evidence_request',
    })
    // WPS-130: a ready-made loop over the items actually being requested on
    // this send. Authoring a fixed list is what made "request additional
    // documentation" letters go stale - the requested items are only known at
    // request time, so the body has to render them rather than name them.
    variables.push({
      label: 'Requested items list',
      value: REQUESTED_ITEMS_BLOCK,
      group: 'evidence_request',
    })
  }
  for (const prompt of prompts) {
    if (!prompt.key) continue
    variables.push({
      label: prompt.label || prompt.key,
      value: promptToken(prompt.key),
      group: 'fill_ins',
    })
  }
  // Cluster by group, preserving first-seen order for stable rendering.
  const groups: { name: string; items: VariableSpec[] }[] = []
  for (const spec of variables) {
    const key = spec.group || 'other'
    const existing = groups.find((g) => g.name === key)
    if (existing) existing.items.push(spec)
    else groups.push({ name: key, items: [spec] })
  }

  // Collapse groups so the picker stays compact - a long catalogue (claim has
  // ~25 fields) is overwhelming all at once. Common/Upload open by default;
  // the rest expand on demand. Toggling uses onMouseDown + preventDefault so
  // the editor keeps focus and cursor-insertion still works.
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(['common', 'evidence_request', 'fill_ins']),
  )
  const toggleGroup = (name: string) =>
    setOpenGroups((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })

  // Insert the variable directly at the cursor of whatever input/editor
  // was last focused - avoids the clipboard entirely. Falls back to
  // clipboard, then to a "paste this manually" toast if even that fails.
  //
  // For link-kind variables we drop an <a> tag when the focus is in the
  // EmailEditor (HTML textarea or TipTap), and fall back to the raw
  // {{ … }} token in plain inputs like the subject line.
  const insertOrCopy = (spec: VariableSpec) => {
    const { value, kind = 'text', linkLabel = 'Link' } = spec
    // 1) Try to insert into the currently-focused input/textarea/contenteditable.
    //    We call this from onMouseDown with preventDefault so focus stays on
    //    the previously-active element.
    const el = document.activeElement as HTMLElement | null
    if (el) {
      const tag = el.tagName
      const isTextarea = tag === 'TEXTAREA'
      const isTextField =
        (tag === 'INPUT' &&
          /^(text|email|search|url|tel)$/i.test(
            (el as HTMLInputElement).type || 'text',
          )) ||
        isTextarea
      const isEditable =
        (el as HTMLElement).isContentEditable === true ||
        el.getAttribute('contenteditable') === 'true'

      // For link/button kinds: drop HTML when we're inside a body editor
      // (textarea in HTML mode or TipTap contenteditable). For a plain
      // <input> like the subject line, HTML wouldn't render - insert the
      // raw token instead.
      const insertAsHtml =
        (kind === 'link' || kind === 'button') && (isTextarea || isEditable)
      const inserted = insertAsHtml
        ? kind === 'button'
          ? buildEmailButton(value, linkLabel)
          : `<a href="${value}">${linkLabel}</a>`
        : value

      if (isTextField) {
        const field = el as HTMLInputElement | HTMLTextAreaElement
        const start = field.selectionStart ?? field.value.length
        const end = field.selectionEnd ?? field.value.length
        const next =
          field.value.slice(0, start) + inserted + field.value.slice(end)
        // Use the native setter so React notices the change.
        const setter = Object.getOwnPropertyDescriptor(
          field.tagName === 'INPUT'
            ? window.HTMLInputElement.prototype
            : window.HTMLTextAreaElement.prototype,
          'value',
        )?.set
        setter?.call(field, next)
        field.dispatchEvent(new Event('input', { bubbles: true }))
        const caret = start + inserted.length
        requestAnimationFrame(() => field.setSelectionRange(caret, caret))
        return
      }

      if (isEditable) {
        // For link/button kinds in TipTap, insertHTML drops the markup;
        // for text variables, insertText fires beforeinput so TipTap
        // captures it. Note: TipTap may strip inline styles on a button
        // anchor - admins should toggle to HTML source mode if they need
        // the full styling preserved end-to-end.
        const ok = insertAsHtml
          ? document.execCommand('insertHTML', false, inserted)
          : document.execCommand('insertText', false, inserted)
        if (ok) return
      }
    }

    // 2) Fall back to clipboard.
    void copyToClipboard(value)
  }

  const copyToClipboard = async (value: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        toast.success(`Copied ${value}`)
        return
      }
    } catch {
      // fall through
    }
    try {
      const ta = document.createElement('textarea')
      ta.value = value
      ta.setAttribute('readonly', '')
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      const ok = document.execCommand('copy')
      document.body.removeChild(ta)
      if (ok) {
        toast.success(`Copied ${value}`)
        return
      }
    } catch {
      // fall through
    }
    toast(`Paste manually: ${value}`)
  }
  const groupLabel = (name: string) =>
    name === 'common'
      ? 'General'
      : name === 'evidence_request'
        ? 'Upload'
        : name === 'fill_ins'
          ? t('emailTemplates.fillIns.title', 'Fill-ins')
          : name.charAt(0).toUpperCase() + name.slice(1)

  return (
    <div className="mt-2 divide-y divide-slate-100 rounded-lg border-[0.5px] border-slate-200">
      {hint && (
        <div className="flex items-center gap-1.5 rounded-t-lg bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500">
          <MousePointerClick size={12} className="shrink-0 text-slate-400" aria-hidden />
          <span>{hint}</span>
        </div>
      )}
      {groups.map((g) => {
        const isOpen = openGroups.has(g.name)
        return (
          <div key={g.name}>
            <Button
              type="button"
              variant="ghost"
              onMouseDown={(e) => {
                // Keep focus on the editor so cursor-insertion still works.
                e.preventDefault()
                toggleGroup(g.name)
              }}
              className="h-auto w-full justify-start gap-2 rounded-none px-3 py-2 font-normal hover:bg-slate-50"
            >
              <ChevronDown
                size={13}
                className={
                  'shrink-0 text-slate-400 transition-transform ' +
                  (isOpen ? '' : '-rotate-90')
                }
              />
              <span className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {groupLabel(g.name)}
              </span>
              <span className="text-[11px] text-slate-400">{g.items.length}</span>
            </Button>
            {isOpen && (
              <div className="flex flex-wrap gap-1.5 px-3 pb-3 pt-0.5">
                {g.items.map((v) => (
                  <Button
                    key={v.value}
                    type="button"
                    variant="ghost"
                    size="xs"
                    onMouseDown={(e) => {
                      // Keep focus on the input/editor so we can insert at its
                      // cursor rather than the button.
                      e.preventDefault()
                      insertOrCopy(v)
                    }}
                    className={
                      'h-auto gap-1 rounded-full px-2.5 py-1 text-[11px] font-normal ' +
                      (v.kind === 'link' || v.kind === 'button'
                        ? 'bg-emerald-50 text-emerald-800 ring-[0.5px] ring-emerald-200 hover:bg-emerald-100'
                        : 'bg-slate-50 text-slate-600 ring-[0.5px] ring-slate-200 hover:bg-slate-100')
                    }
                    title={
                      v.kind === 'button'
                        ? `Insert as button (CTA): ${v.value}`
                        : v.kind === 'link'
                          ? `Insert as link: ${v.value}`
                          : `Insert ${v.value}`
                    }
                  >
                    {v.label}
                    {(v.kind === 'link' || v.kind === 'button') && (
                      <span className="text-[9px] uppercase tracking-wider opacity-70">
                        {v.kind}
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Advanced section ──────────────────────────────────────────────

function AdvancedSection({
  open,
  onToggle,
  form,
  update,
  customisePlainText,
  setCustomisePlainText,
}: {
  open: boolean
  onToggle: () => void
  form: TemplateDetail
  update: (patch: Partial<TemplateDetail>) => void
  customisePlainText: boolean
  setCustomisePlainText: (v: boolean) => void
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left"
      >
        <div className="flex items-center gap-2">
          <ChevronRight
            size={14}
            className={`text-slate-400 transition-transform ${
              open ? 'rotate-90' : ''
            }`}
          />
          <span className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Advanced
          </span>
        </div>
        <span className="text-xs text-slate-400">
          {open ? 'Hide' : 'For engineers'}
        </span>
      </button>
      {open && (
        <div className="px-5 md:px-6 pb-5 md:pb-6 space-y-5 border-t border-slate-100 pt-4">
          <div>
            <Label>Tracking code (optional)</Label>
            <Input
              value={form.tracking_code}
              onChange={(e) => update({ tracking_code: e.target.value })}
              placeholder="e.g. CL-BLOW-A1"
              className="font-mono text-sm"
            />
            <p className="text-xs text-slate-500 mt-1">
              Stamped on the PDF footer for audit. Used to trace a printed
              letter back to a tenant + template version.
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={customisePlainText}
                onChange={(e) => setCustomisePlainText(e.target.checked)}
              />
              Customise plain-text version
            </label>
            <p className="text-xs text-slate-500 mt-1">
              By default we generate the plain-text version of the email
              automatically. Tick this if you want to write it yourself.
            </p>
            {customisePlainText && (
              <textarea
                value={form.body_text}
                onChange={(e) => update({ body_text: e.target.value })}
                rows={4}
                className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
                placeholder="Plain-text alternative for clients that don’t render HTML."
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Output modes editor ───────────────────────────────────────────

const OUTPUT_MODE_OPTIONS: Array<{
  value: OutputMode
  label: string
  description: string
}> = [
  {
    value: 'email_body',
    label: 'Email body',
    description: 'Send the message as a normal email.',
  },
  {
    value: 'pdf_attachment',
    label: 'PDF attachment',
    description: 'Attach a branded PDF letter alongside the email.',
  },
  {
    value: 'upload_page',
    label: 'Upload page',
    description:
      'Adds a private upload link to the email so the recipient can send you photos and documents. Needed for evidence packets.',
  },
]

function OutputModesEditor({
  form,
  update,
  disabled = false,
}: {
  form: TemplateDetail
  update: (patch: Partial<TemplateDetail>) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  // An empty stored list still sends the body - the renderer resolves it to
  // email_body - so show it ticked rather than as "nothing selected", which
  // made two identically-behaving templates look different (WPS-134).
  const stored = form.output_modes ?? []
  const modes = new Set<OutputMode>(stored.length ? stored : ['email_body'])
  const toggle = (mode: OutputMode) => {
    const next = new Set(modes)
    if (next.has(mode)) {
      next.delete(mode)
    } else {
      next.add(mode)
    }
    // Always keep at least one mode - fall back to email_body if the
    // admin un-ticks the last one.
    if (next.size === 0) next.add('email_body')
    update({ output_modes: Array.from(next) as OutputMode[] })
  }
  // upload_page only makes sense for evidence packets; greying it out
  // for plain notifications avoids confusing the renderer.
  const uploadDisabled = form.kind !== 'evidence_request'
  // A letter's PDF is the letter itself, so it cannot be switched off.
  const isLetter = form.kind === 'letter'
  if (isLetter) modes.add('pdf_attachment')

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {OUTPUT_MODE_OPTIONS.map((opt) => {
          const checked = modes.has(opt.value)
          // Disable a card when the editor is read-only (the `disabled` prop,
          // e.g. an org override) OR when it's the evidence-only upload_page on
          // a non-evidence template. Named to avoid shadowing the prop.
          const optDisabled =
            disabled || (opt.value === 'upload_page' && uploadDisabled)
          // Selected and fixed: shown as chosen, not as unavailable.
          const locked = opt.value === 'pdf_attachment' && isLetter
          return (
            // A toggle card: pressed = the mode is on. A locked card is not
            // `disabled`, so it reads as chosen rather than faded.
            <Button
              key={opt.value}
              type="button"
              variant="ghost"
              aria-pressed={checked}
              aria-disabled={locked || undefined}
              onClick={() => !optDisabled && !locked && toggle(opt.value)}
              disabled={optDisabled}
              className={cn(
                'h-auto w-full flex-col items-stretch justify-start gap-0 whitespace-normal rounded-lg border border-solid px-3 py-2.5 text-left font-normal transition-colors hover:bg-transparent',
                optDisabled
                  ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
                  : locked
                    ? 'border-slate-900 bg-slate-50 cursor-default'
                    : checked
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300',
              )}
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn(
                    'flex size-3.5 shrink-0 items-center justify-center rounded-[3px] border',
                    checked
                      ? 'border-slate-900 bg-slate-900 text-white'
                      : 'border-slate-300 bg-white',
                  )}
                >
                  {checked && <Check className="size-2.5" />}
                </span>
                <span className="text-sm font-medium text-slate-900">{opt.label}</span>
              </span>
              <span className="mt-1 block text-xs leading-snug text-slate-500">
                {opt.description}
              </span>
            </Button>
          )
        })}
      </div>
      {uploadDisabled && (
        <p className="text-xs text-slate-400 mt-2">
          {disabled
            ? t(
                'emailTemplates.uploadDisabledDefault',
                "Upload page is only available for evidence packets. This template's type follows the platform default and cannot be changed here.",
              )
            : t(
                'emailTemplates.uploadDisabledSwitch',
                'Upload page is only available for evidence packets - switch the template type to enable it.',
              )}
        </p>
      )}
      {isLetter && (
        <p className="text-xs text-slate-400 mt-2">
          {t(
            'emailTemplates.pdfAlwaysOnLetter',
            'A letter always carries its PDF. A letter about a claim is also kept on the claim and can be posted.',
          )}
        </p>
      )}
      {/* Uploaded PDFs go with every send, whatever the output modes. */}
      <TemplateAttachmentsEditor
        attachments={form.attachments ?? []}
        onChange={(next) => update({ attachments: next })}
        disabled={disabled}
        enclosures={isLetter}
      />
    </div>
  )
}

export default function EmailTemplateEditor() {
  const { t } = useTranslation()
  const { id } = useParams<{ id?: string }>()
  const isNew = id === 'new'
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // For new templates: ?kind=notification|evidence_request preselects the
  // section the admin came from (top half vs bottom half of the merged tab).
  const requestedKind = searchParams.get('kind')
  const initialKind: TemplateKind =
    requestedKind === 'evidence_request' || requestedKind === 'letter'
      ? requestedKind
      : 'notification'

  const { data: existing, isLoading } = useEmailTemplate(isNew ? undefined : id)
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate(id ?? '')
  const deleteMutation = useDeleteTemplate()
  const saveOverride = useSaveTemplateOverride(id ?? '')
  const deleteOverride = useDeleteTemplateOverride(id ?? '')

  // Draft tracks the user's edits on top of either the blank template (new) or
  // the loaded server data (existing). We never mirror server data into a
  // local state via useEffect - `form` is a derived value.
  const [draft, setDraft] = useState<Partial<TemplateDetail>>({})
  // True after "Customise for my organisation" is clicked but before the
  // first override save creates the row.
  const [customising, setCustomising] = useState(false)
  // Plain-text body is hidden unless explicitly customised.
  const [customisePlainText, setCustomisePlainText] = useState(false)
  // Advanced section collapsed by default.
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form: TemplateDetail | null = useMemo(() => {
    if (isNew) {
      const seeded: TemplateDetail = {
        ...BLANK_TEMPLATE,
        kind: initialKind,
        structured_payload:
          initialKind === 'evidence_request' ? EMPTY_PAYLOAD : {},
        // Evidence packets always need an upload session minted alongside
        // the email body - otherwise {{ session.url }} resolves to nothing
        // and the renderer has no slots to capture uploads into.
        output_modes:
          initialKind === 'evidence_request'
            ? ['email_body', 'upload_page']
            : initialKind === 'letter'
              ? ['email_body', 'pdf_attachment']
              : ['email_body'],
      }
      return { ...seeded, ...draft }
    }
    if (!existing) return null
    // When the org has an override, the editor surfaces ITS content -
    // that's what actually sends for this organisation.
    const base = existing.org_override
      ? {
          ...existing,
          // Prefer the org override's own name; fall back to the base
          // template name when the override hasn't set one.
          name: existing.org_override.name || existing.name,
          // A blank override field INHERITS the platform's (see the backend's
          // _resolve_content_template). Show what will actually send rather
          // than an empty box; `inherited_fields` says which those are, so a
          // field left untouched is saved back as blank and keeps inheriting.
          subject: existing.org_override.subject || existing.subject,
          body_html: existing.org_override.body_html || existing.body_html,
          body_text: existing.org_override.body_text || existing.body_text,
        }
      : existing
    return { ...base, ...draft }
  }, [isNew, existing, draft, initialKind])

  const smsRef = useRef<HTMLTextAreaElement>(null)
  // Saved fill-in keys may already be used in the body, so they stay fixed.
  const lockedPromptKeys = useMemo(
    () => new Set((existing?.prompts ?? []).map((prompt) => prompt.key)),
    [existing],
  )

  if (!form) return <p className="text-sm text-slate-500">Loading…</p>
  if (!isNew && isLoading) return <p className="text-sm text-slate-500">Loading…</p>

  const update = (patch: Partial<TemplateDetail>) =>
    setDraft((d) => ({ ...d, ...patch }))

  // SMS is plain text, so it gets its own small token set - the rich email
  // palette inserts HTML / the raw session URL, which are wrong for a text.
  const smsTokens: { token: string; label: string }[] = [
    { token: '{{ recipient_name }}', label: 'Recipient name' },
    { token: '{{ upload_link }}', label: 'Upload link' },
    { token: '{{ claim_reference }}', label: 'Claim reference' },
  ]
  const insertSmsToken = (token: string) => {
    const el = smsRef.current
    const current = form?.sms_body ?? ''
    if (!el) {
      update({ sms_body: current + token })
      return
    }
    const start = el.selectionStart ?? current.length
    const end = el.selectionEnd ?? current.length
    update({ sms_body: current.slice(0, start) + token + current.slice(end) })
    requestAnimationFrame(() => {
      el.focus()
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
    })
  }

  const handleKindChange = (next: TemplateKind) => {
    if (form.kind === next) return
    update({
      kind: next,
      structured_payload: next === 'evidence_request' ? EMPTY_PAYLOAD : {},
      // Auto-promote output modes so admins who flip to "evidence packet"
      // immediately get an upload session minted on send.
      output_modes:
        next === 'evidence_request'
          ? Array.from(new Set([...(form.output_modes ?? []), 'upload_page']))
          : next === 'letter'
            ? Array.from(
                new Set([
                  ...(form.output_modes ?? ['email_body']).filter(
                    (m) => m !== 'upload_page',
                  ),
                  'pdf_attachment' as const,
                ]),
              )
            : (form.output_modes ?? ['email_body']).filter(
                (m) => m !== 'upload_page',
              ),
    })
  }

  const isPlatform = !isNew && form.is_platform
  const isSlugBound = form.is_slug_bound
  const hasOverride = Boolean(existing?.org_override)
  const overrideMode = isPlatform && isSlugBound && (hasOverride || customising)

  // Fields the override does not carry, so they follow the platform template.
  const inheritedFields = existing?.inherited_fields ?? []

  /**
   * The override payload. A field that is inherited and that the admin has not
   * touched is sent BLANK, so displaying the platform's text (above) never
   * silently turns it into their own frozen copy. `overrides` forces a value -
   * the per-field reset passes '' to hand a field back to the platform.
   */
  const overridePayload = (overrides: Record<string, string> = {}) => {
    const field = (name: 'subject' | 'body_html' | 'body_text') => {
      if (name in overrides) return overrides[name]
      if (inheritedFields.includes(name) && !(name in draft)) return ''
      return form[name] ?? ''
    }
    return {
      name: form.name,
      subject: field('subject'),
      body_html: field('body_html'),
      body_text: field('body_text'),
    }
  }

  const handleResetField = async (
    name: 'subject' | 'body_html' | 'body_text',
    label: string,
  ) => {
    try {
      await saveOverride.mutateAsync(overridePayload({ [name]: '' }))
      setDraft((d) => {
        const next = { ...d }
        delete next[name]
        return next
      })
      toast.success(`${label} now follows the platform template.`)
    } catch {
      toast.error("Couldn't reset. Try again.")
    }
  }

  const handleSave = async () => {
    if (overrideMode) {
      try {
        await saveOverride.mutateAsync(overridePayload())
        setDraft({})
        setCustomising(false)
        toast.success("Saved your organisation's version.")
      } catch {
        toast.error("Couldn't save. Try again.")
      }
      return
    }
    try {
      if (isNew) {
        const created = await createMutation.mutateAsync(form)
        toast.success('Template created.')
        navigate(`/settings/email-templates/${created.id}`, { replace: true })
      } else {
        const saved = await updateMutation.mutateAsync(form)
        // Editing a platform default copies-on-write into the tenant's own row,
        // which has a new id - follow it so the editor shows the saved copy.
        if (saved?.id && saved.id !== form.id) {
          toast.success(
            t(
              'emailTemplates.editor.savedAsCopy',
              "Saved as your organisation's copy.",
            ),
          )
          navigate(`/settings/email-templates/${saved.id}`, { replace: true })
        } else {
          toast.success(`Saved as version ${form.version + 1}.`)
        }
      }
    } catch (err: unknown) {
      toast.error(describeSaveError(err))
    }
  }

  const handleDelete = async () => {
    if (isNew || !existing) return
    try {
      await deleteMutation.mutateAsync({ id: existing.id })
      toast.success('Template disabled.')
      navigate('/settings/email-templates')
    } catch {
      toast.error("Couldn't disable template.")
    }
  }

  const handleEnable = async () => {
    if (isNew || !existing) return
    try {
      await updateMutation.mutateAsync({ ...form, is_active: true })
      update({ is_active: true })
      toast.success('Template enabled.')
    } catch {
      toast.error("Couldn't enable template.")
    }
  }

  const handleRevert = async () => {
    try {
      await deleteOverride.mutateAsync()
      setDraft({})
      setCustomising(false)
      toast.success('Reverted to the platform default.')
    } catch {
      toast.error("Couldn't revert. Try again.")
    }
  }

  return (
    <div className="space-y-5 pb-12">
      {/* Sticky action bar: keeps Save (and Preview / Test send) in view while
          the admin scrolls the long form - e.g. after adding a PDF at the
          bottom - so changes can't be left unsaved by accident. Full-bleed via
          negative margins that cancel the settings pane's padding (p-4 mobile,
          px-8 py-6 desktop). The `::before` fills that same padding band above
          the bar with an OPAQUE bg so, once pinned, scrolling content (e.g. the
          HTML body editor) can't show through the gap `sticky top-0` leaves at
          the pane's inner padding edge. `bg` (not `bg-light`, which is 98%
          opaque) is used so nothing ghosts through. */}
      <div className="sticky top-0 z-20 -mx-4 -mt-4 border-b-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-3 before:absolute before:inset-x-0 before:bottom-full before:h-4 before:bg-[var(--bg)] before:content-[''] lg:-mx-8 lg:-mt-6 lg:px-8 lg:py-4 lg:before:h-6">
        <Header
          form={form}
          isNew={isNew}
          isPlatform={isPlatform}
          canDuplicate={isPlatform}
          overrideMode={overrideMode}
          hasOverride={hasOverride}
          isBehindBase={Boolean(existing?.is_behind_base)}
          inheritedFields={existing?.inherited_fields ?? []}
          templateId={isNew ? null : form.id || Number(id)}
          // The live editor content, so Preview renders what is on screen.
          draft={{
            subject: form.subject ?? '',
            body_html: form.body_html ?? '',
            body_text: form.body_text ?? '',
          }}
          onBack={() => navigate('/settings/email-templates')}
          onSave={handleSave}
          onDelete={handleDelete}
          onEnable={handleEnable}
          onCustomise={() => setCustomising(true)}
          onRevert={handleRevert}
          saving={
            createMutation.isPending ||
            updateMutation.isPending ||
            saveOverride.isPending
          }
          reverting={deleteOverride.isPending}
        />
      </div>

      {isPlatform && !overrideMode && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          {t(
            'emailTemplates.editor.platformDefaultWarning',
            "This is a platform default. Saving your changes creates your organisation's own editable copy - other organisations keep the default untouched.",
          )}
        </div>
      )}
      {overrideMode && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-900">
          You're editing your organisation's version. The name, subject and
          message are yours - everything else follows the platform template.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
          Basics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => {
                const next: Partial<TemplateDetail> = { name: e.target.value }
                // Auto-fill slug from the name for new templates. The slug
                // input is no longer admin-visible, so the name is the only
                // source. (Slug is locked server-side after first save.)
                if (isNew) {
                  next.slug = slugify(e.target.value)
                }
                update(next)
              }}
              placeholder="XYZ Email template"
            />
          </div>
          <div>
            <Label>Type</Label>
            <Select
              value={form.kind}
              onValueChange={(v: string) => handleKindChange(v as TemplateKind)}
              disabled={overrideMode}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="notification">Plain email</SelectItem>
                <SelectItem value="evidence_request">
                  Evidence packet (with upload link)
                </SelectItem>
                <SelectItem value="letter">
                  {t('emailTemplates.kindLetter', 'Letter (sent as a PDF)')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Tags</Label>
            <TagsInput
              tags={form.tags ?? []}
              onChange={(next) => update({ tags: next })}
            />
            <p className="text-xs text-slate-500 mt-1">
              Categorisation labels - e.g.{' '}
              <span className="font-mono">Fault: Breakage</span>,{' '}
              <span className="font-mono">Notification</span>,{' '}
              <span className="font-mono">Auth: Login</span>. Press Enter to add. Other
              admins' tags are suggested as you type.
            </p>
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between gap-2">
              <Label>Subject line</Label>
              <FieldInheritance
                inherited={inheritedFields.includes('subject')}
                show={overrideMode}
                busy={saveOverride.isPending}
                onReset={() => handleResetField('subject', 'Subject line')}
              />
            </div>
            <Input
              value={form.subject}
              onChange={(e) => update({ subject: e.target.value })}
              placeholder="e.g. Photos needed for your claim"
            />
            <VariablesPicker
              templateKind={form.kind}
              prompts={form.kind === 'letter' ? (form.prompts ?? []) : []}
              hint="Click into Subject or Message first, then click a variable to drop it in at the cursor."
            />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-end">
              <FieldInheritance
                inherited={inheritedFields.includes('body_html')}
                show={overrideMode}
                busy={saveOverride.isPending}
                onReset={() => handleResetField('body_html', 'Message')}
              />
            </div>
            <EmailEditor
              label="Message"
              value={form.body_html}
              onChange={(html) => update({ body_html: html })}
            />
            <p className="text-xs text-slate-500 mt-2">
              This is the main body of your email. Variables like{' '}
              <code className="bg-slate-100 rounded px-1 text-[11px]">
                {'{{ recipient_name }}'}
              </code>{' '}
              get filled in when each email is sent.
              {form.kind === 'evidence_request' && (
                <>
                  {' '}
                  For evidence packets, the upload link is added automatically -
                  you don't need to include it.
                </>
              )}
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>SMS version</Label>
            <Textarea
              ref={smsRef}
              value={form.sms_body ?? ''}
              onChange={(e) => update({ sms_body: e.target.value })}
              rows={3}
              placeholder="Hi {{ recipient_name }}, upload your photos: {{ upload_link }}"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {smsTokens.map((t) => (
                <Button
                  key={t.token}
                  type="button"
                  variant="outline"
                  size="xs"
                  onClick={() => insertSmsToken(t.token)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Optional, plain text only - any formatting is stripped and links
              are auto-shortened. Use the tokens above;{' '}
              <code className="bg-slate-100 rounded px-1 text-[11px]">
                {'{{ upload_link }}'}
              </code>{' '}
              is the shortened upload link. Don't paste the email's link button
              here.
            </p>
          </div>
        </div>
      </div>

      {/* Output modes - top-level, not hidden under Advanced. Picking
          modes is a routine authoring decision (does this template
          render as email body, PDF, upload page, or some combination?)
          and the admin almost always wants to see / tweak it before
          saving - especially for evidence packets where forgetting the
          "Upload page" mode silently breaks the {{ session.url }}
          link in the email body. */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            What gets sent
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Pick one or more. You can send a plain email, attach a PDF,
            include an upload link, or any combination.
          </p>
        </div>
        <OutputModesEditor
          form={form}
          update={update}
          disabled={overrideMode}
        />
      </div>

      {form.kind === 'letter' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-4">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              {t('emailTemplates.fillIns.title', 'Fill-ins')}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {t(
                'emailTemplates.fillIns.description',
                'Details the sender completes when sending this letter from a claim, for example the defect picked from a list. Drop each one into the subject or message from the variables above.',
              )}
            </p>
          </div>
          <PromptsEditor
            value={form.prompts ?? []}
            onChange={(next) => update({ prompts: next })}
            lockedKeys={lockedPromptKeys}
            disabled={overrideMode}
          />
        </div>
      )}

      {form.kind === 'evidence_request' && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 md:p-6 space-y-5">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
              What to collect
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Set the documents and photos you want from the recipient. Each one
              becomes a card on the upload page they see when they click the link
              in your email.
            </p>
          </div>
          <RequirementsEditor
            value={(form.structured_payload as EvidenceRequestPayload) ?? EMPTY_PAYLOAD}
            onChange={(next) => update({ structured_payload: next })}
          />
        </div>
      )}

      <AdvancedSection
        open={advancedOpen}
        onToggle={() => setAdvancedOpen((v) => !v)}
        form={form}
        update={update}
        customisePlainText={customisePlainText}
        setCustomisePlainText={setCustomisePlainText}
      />
    </div>
  )
}

// ─── Header ────────────────────────────────────────────────────────

function Header({
  form,
  isNew,
  isPlatform,
  canDuplicate,
  overrideMode,
  hasOverride,
  isBehindBase,
  inheritedFields,
  templateId,
  draft,
  onBack,
  onSave,
  onDelete,
  onEnable,
  onCustomise,
  onRevert,
  saving,
  reverting,
}: {
  form: TemplateDetail
  isNew: boolean
  isPlatform: boolean
  canDuplicate: boolean
  overrideMode: boolean
  hasOverride: boolean
  isBehindBase: boolean
  inheritedFields: string[]
  templateId: number | null
  draft: { subject: string; body_html: string; body_text: string }
  onBack: () => void
  onSave: () => void
  onDelete: () => void
  onEnable: () => void
  onCustomise: () => void
  onRevert: () => void
  saving: boolean
  reverting: boolean
}) {
  return (
    <header className="flex items-start justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3 min-w-0">
        <Button variant="ghost" size="icon-sm" onClick={onBack} aria-label="Back">
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-slate-900 truncate">
            {isNew ? 'New email template' : form.name || form.slug}
          </h1>
          {!isNew && (
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary">{form.kind}</Badge>
              <span className="text-xs text-slate-500 tabular-nums">v{form.version}</span>
              {isPlatform &&
                (hasOverride ? (
                  <Badge variant="secondary">Customised - your organisation</Badge>
                ) : (
                  <Badge variant="outline">Platform default</Badge>
                ))}
              {/* A full copy inherits nothing, so it misses later platform
                  changes. Say so rather than let the drift stay invisible -
                  re-adopting is the admin's call, not ours. */}
              {isBehindBase && (
                <Badge variant="destructive">Behind the platform default</Badge>
              )}
              {hasOverride && inheritedFields.length > 0 && (
                <Badge variant="outline">
                  Follows the platform:{' '}
                  {inheritedFields.map((f) => FIELD_LABELS[f] ?? f).join(', ')}
                </Badge>
              )}
              {!form.is_active && <Badge variant="outline">Disabled</Badge>}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Preview + Test send sit next to Disable/Save so admins don't
            have to scroll the whole editor to inspect their changes. */}
        {templateId && (
          <>
            <PreviewButton templateId={templateId} draft={draft} />
            <TestSendButton templateId={templateId} />
          </>
        )}
        {overrideMode ? (
          <>
            {hasOverride && (
              <Button variant="ghost" onClick={onRevert} disabled={reverting}>
                <RefreshCw size={14} />
                {reverting ? 'Reverting…' : 'Revert to platform default'}
              </Button>
            )}
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </>
        ) : isPlatform ? (
          canDuplicate ? (
            // Non-slug-bound platform default: saving copies-on-write into the
            // tenant's own editable row, so the primary action is just Save.
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          ) : (
            <Button onClick={onCustomise}>
              <Paintbrush size={14} />
              Customise
            </Button>
          )
        ) : (
          <>
            {!isNew && form.is_active && (
              <Button variant="ghost" onClick={onDelete}>
                <Trash2 size={14} />
                Disable
              </Button>
            )}
            {!isNew && !form.is_active && (
              <Button variant="outline" onClick={onEnable}>
                <Power size={14} />
                Enable
              </Button>
            )}
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
            </Button>
          </>
        )}
      </div>
    </header>
  )
}

// ─── Preview + test send ───────────────────────────────────────────

function PreviewButton({
  templateId,
  draft,
}: {
  templateId: number
  draft: { subject: string; body_html: string; body_text: string }
}) {
  // We DON'T pre-fire the preview here - opening the dialog mounts the
  // shared PreviewDialog which fires its own request, the same one the
  // list-page eye button uses. Keeps both surfaces identical.
  const [open, setOpen] = useState(false)
  const template = useEmailTemplate(open ? templateId : undefined)

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Eye size={14} />
        Preview
      </Button>
      {open && template.data && (
        <PreviewDialog
          template={{
            id: template.data.id,
            name: template.data.name,
            slug: template.data.slug,
            kind: template.data.kind,
          }}
          draft={draft}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function TestSendButton({ templateId }: { templateId: number }) {
  const { t } = useTranslation()
  const testSend = useTestSendTemplate(templateId)
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')

  const handleSend = async () => {
    if (!email) {
      toast.error('Enter a recipient email first.')
      return
    }
    try {
      const res = await testSend.mutateAsync({ recipient_email: email })
      const count = res?.attachment_count ?? 0
      const unavailable = res?.attachments_unavailable ?? 0
      // The email still sends without a PDF whose file cannot be read, so say
      // so - otherwise a bare test email looks like the template is fine.
      if (unavailable > 0) {
        toast.warning(
          t('emailTemplates.testSend.attachmentUnavailable', {
            email,
            count: unavailable,
          }),
        )
        setOpen(false)
        return
      }
      toast.success(
        count > 0
          ? t(
              'emailTemplates.testSend.successWithPdf',
              'Test email sent to {{email}} with {{count}} PDF attachment{{plural}}.',
              { email, count, plural: count === 1 ? '' : 's' },
            )
          : t('emailTemplates.testSend.success', 'Test email sent to {{email}}.', { email }),
      )
      setOpen(false)
    } catch {
      toast.error("Couldn't send test email.")
    }
  }

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>
        <Send size={14} />
        Test send
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send a test email</DialogTitle>
            <DialogDescription>
              Renders the template with an empty context (plus a sample session URL for evidence
              packets) and emails it. Subject is prefixed with [TEST]. No upload session is
              created.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Recipient email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </div>
          <Separator />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={testSend.isPending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={testSend.isPending}>
              {testSend.isPending ? 'Sending…' : 'Send test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ─── Tag input (chip + autocomplete from tenant tag pool) ──────

function TagsInput({
  tags,
  onChange,
}: {
  tags: string[]
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')
  const { data: suggestions = [] } = useEmailTemplateTags()

  const matching = draft.trim()
    ? suggestions.filter(
        (s) =>
          !tags.includes(s) &&
          s.toLowerCase().includes(draft.trim().toLowerCase()),
      )
    : suggestions.filter((s) => !tags.includes(s)).slice(0, 8)

  const add = (raw: string) => {
    const value = raw.trim()
    if (!value || tags.includes(value)) {
      setDraft('')
      return
    }
    onChange([...tags, value])
    setDraft('')
  }
  const remove = (t: string) => onChange(tags.filter((x) => x !== t))

  return (
    <div className="mt-1 rounded-md border border-slate-300 px-2 py-1.5 focus-within:border-slate-500 transition-colors">
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 text-[11px] tracking-wide px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-700"
          >
            {t}
            <button
              type="button"
              onClick={() => remove(t)}
              className="text-slate-500 hover:text-slate-900 leading-none"
              aria-label={`Remove ${t}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              e.preventDefault()
              add(draft)
            } else if (e.key === 'Backspace' && draft === '' && tags.length) {
              remove(tags[tags.length - 1])
            }
          }}
          onBlur={() => {
            if (draft.trim()) add(draft)
          }}
          placeholder={tags.length ? '' : 'Add a tag and press Enter'}
          className="flex-1 min-w-[140px] text-sm bg-transparent outline-none py-0.5 px-1"
        />
      </div>
      {matching.length > 0 && draft.trim() && (
        <div className="mt-1 border-t border-slate-100 pt-1 flex flex-wrap gap-1">
          {matching.slice(0, 6).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="text-[11px] px-2 py-0.5 rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Whether one override field follows the platform template, and the control to
 * hand it back.
 *
 * "Revert to platform default" deletes the WHOLE override, so an admin told
 * their copy was behind had to discard their subject to pick up a body fix.
 * Because a blank override field inherits, handing one field back is just
 * clearing it - so this is the proportionate remedy the badge was missing.
 */
function FieldInheritance({
  inherited,
  show,
  busy,
  onReset,
}: {
  inherited: boolean
  show: boolean
  busy: boolean
  onReset: () => void
}) {
  if (!show) return null
  if (inherited) {
    return (
      <span className="text-xs text-slate-500">Follows the platform template</span>
    )
  }
  return (
    <Button variant="ghost" size="xs" onClick={onReset} disabled={busy}>
      <RefreshCw size={12} />
      Reset to platform
    </Button>
  )
}
